import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { ChevronLeft, ChevronRight, Plus, ExternalLink, X, Clock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmtDate(d) { return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); }

export default function Agenda() {
  const { user } = useAuth();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const range = useMemo(() => {
    if (view === "week") {
      const start = startOfWeek(cursor);
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return { start, end };
    } else {
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      return { start, end };
    }
  }, [view, cursor]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sessions", { params: { start: range.start.toISOString(), end: range.end.toISOString() } });
      setSessions(data);
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
    setClients((prev) => {
      if (prev.length > 0) return prev;
      api.get("/clients").then((c) => setClients(c.data)).catch((e) => console.error("Failed to load clients", e));
      return prev;
    });
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const sessionsByDay = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      const k = new Date(s.starts_at).toDateString();
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
    return map;
  }, [sessions]);

  const onPrev = () => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCursor(d);
  };
  const onNext = () => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCursor(d);
  };

  const openCreate = (preset) => {
    setEditing({
      client_id: clients[0]?.id || "",
      starts_at: preset || new Date().toISOString().slice(0, 16),
      duration_min: 60, price: user?.default_session_price ?? 60, session_type: "Séance individuelle",
      status: "scheduled", payment_status: "pending", notes: "",
    });
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing({ ...s, starts_at: new Date(s.starts_at).toISOString().slice(0, 16) });
    setShowModal(true);
  };

  const save = async () => {
    setError("");
    const payload = {
      ...editing,
      starts_at: new Date(editing.starts_at).toISOString(),
      price: Number(editing.price),
      duration_min: Number(editing.duration_min),
    };
    try {
      if (editing.id) {
        await api.put(`/sessions/${editing.id}`, payload);
      } else {
        await api.post("/sessions", payload);
      }
      setShowModal(false);
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!editing?.id) return;
    if (!window.confirm("Supprimer cette séance ?")) return;
    await api.delete(`/sessions/${editing.id}`);
    setShowModal(false);
    setEditing(null);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="agenda-container">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Agenda</h1>
          <p className="text-muted-soft mt-1">Vos séances, semaine après semaine.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.google_calendar_url && (
            <a href={user.google_calendar_url} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="google-calendar-link">
              <ExternalLink className="inline w-4 h-4 mr-1.5" strokeWidth={1.6} />
              Google Calendar
            </a>
          )}
          <button onClick={() => openCreate()} className="btn-sage text-sm" data-testid="add-session-btn">
            <Plus className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Ajouter une séance
          </button>
        </div>
      </div>

      <div className="card-soft p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <button onClick={onPrev} className="p-2 rounded-full hover:bg-sage-light" data-testid="agenda-prev"><ChevronLeft className="w-4 h-4" strokeWidth={1.6} /></button>
            <button onClick={onNext} className="p-2 rounded-full hover:bg-sage-light" data-testid="agenda-next"><ChevronRight className="w-4 h-4" strokeWidth={1.6} /></button>
            <div className="font-display text-lg ml-2">
              {view === "week"
                ? `${fmtDate(range.start)} — ${fmtDate(new Date(range.end.getTime() - 1))}`
                : cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
            </div>
          </div>
          <div className="flex p-1 rounded-full" style={{ backgroundColor: "var(--sage-light)" }}>
            <button onClick={() => setView("week")} data-testid="view-week" className={`px-4 py-1.5 text-sm rounded-full transition-all ${view === "week" ? "bg-white text-sage font-medium shadow-sm" : "text-muted-soft"}`}>Semaine</button>
            <button onClick={() => setView("month")} data-testid="view-month" className={`px-4 py-1.5 text-sm rounded-full transition-all ${view === "month" ? "bg-white text-sage font-medium shadow-sm" : "text-muted-soft"}`}>Mois</button>
          </div>
        </div>

        {view === "week" ? <WeekGrid range={range} sessionsByDay={sessionsByDay} onSlot={openCreate} onSession={openEdit} /> :
          <MonthGrid cursor={cursor} sessionsByDay={sessionsByDay} onSession={openEdit} onDay={(d) => openCreate(new Date(d).toISOString().slice(0, 16))} />}
      </div>

      {showModal && editing && (
        <Modal onClose={() => { setShowModal(false); setEditing(null); }} title={editing.id ? "Modifier la séance" : "Nouvelle séance"} testid="session-modal">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">Client</label>
              {clients.length === 0 ? (
                <div className="text-sm text-muted-soft">Ajoutez un client d'abord depuis l'onglet Clients.</div>
              ) : (
                <select className="input-soft" value={editing.client_id} onChange={(e) => setEditing({ ...editing, client_id: e.target.value })} data-testid="session-client">
                  <option value="">Choisir…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-2">Date & heure</label>
                <input type="datetime-local" className="input-soft" value={editing.starts_at}
                  onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} data-testid="session-datetime" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Durée (min)</label>
                <input type="number" className="input-soft" value={editing.duration_min}
                  onChange={(e) => setEditing({ ...editing, duration_min: e.target.value })} data-testid="session-duration" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-2">Type de séance</label>
                <input type="text" className="input-soft" value={editing.session_type}
                  onChange={(e) => setEditing({ ...editing, session_type: e.target.value })} data-testid="session-type" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Tarif (€)</label>
                <input type="number" step="0.01" className="input-soft" value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="session-price" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-2">Statut</label>
                <select className="input-soft" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="session-status">
                  <option value="scheduled">Prévue</option>
                  <option value="done">Réalisée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Paiement</label>
                <select className="input-soft" value={editing.payment_status} onChange={(e) => setEditing({ ...editing, payment_status: e.target.value })} data-testid="session-payment">
                  <option value="pending">En attente</option>
                  <option value="paid">Payée</option>
                  <option value="to_chase">À relancer</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Notes</label>
              <textarea className="input-soft" style={{ height: 90, paddingTop: 12 }} value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="session-notes" />
            </div>
            {error && <div className="text-sm text-clay">{error}</div>}
            <div className="flex justify-between gap-3 pt-2">
              {editing.id ? <button onClick={remove} className="text-sm text-clay hover:underline" data-testid="session-delete">Supprimer</button> : <span />}
              <div className="flex gap-2">
                <button onClick={() => { setShowModal(false); setEditing(null); }} className="btn-ghost text-sm" data-testid="session-cancel">Annuler</button>
                <button onClick={save} className="btn-sage text-sm" data-testid="session-save" disabled={!editing.client_id}>Enregistrer</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function WeekGrid({ range, sessionsByDay, onSlot, onSession }) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(range.start); d.setDate(d.getDate() + i);
    days.push(d);
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-3">
      {days.map((d, i) => {
        const list = sessionsByDay[d.toDateString()] || [];
        const isToday = d.toDateString() === new Date().toDateString();
        return (
          <div key={d.toISOString()} className="rounded-xl p-3 min-h-[180px] border" style={{ borderColor: isToday ? "var(--sage)" : "var(--border)", backgroundColor: isToday ? "var(--sage-light)" : "var(--surface)" }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs text-muted-soft">{DAYS[i]}</div>
                <div className="font-display text-lg">{d.getDate()}</div>
              </div>
              <button onClick={() => onSlot(new Date(d.setHours(9,0,0,0)).toISOString().slice(0, 16))} className="p-1.5 rounded-full hover:bg-white" data-testid={`add-session-day-${i}`}>
                <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </div>
            <div className="space-y-1.5">
              {list.length === 0 && <div className="text-xs text-muted-soft">Rien de prévu.</div>}
              {list.map((s) => <SessionPill key={s.id} s={s} onClick={() => onSession(s)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthGrid({ cursor, sessionsByDay, onSession, onDay }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(d.getDate() + i);
    cells.push(d);
  }
  return (
    <div>
      <div className="grid grid-cols-7 gap-2 text-xs text-muted-soft mb-2">
        {DAYS.map((d) => <div key={d} className="px-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          const list = sessionsByDay[d.toDateString()] || [];
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <button onClick={() => onDay(d)} key={d.toISOString()} className="rounded-xl p-2 min-h-[90px] border text-left hover:bg-sage-light transition-all"
              style={{ borderColor: isToday ? "var(--sage)" : "var(--border)", backgroundColor: inMonth ? "var(--surface)" : "transparent", opacity: inMonth ? 1 : 0.5 }}>
              <div className={`text-sm ${isToday ? "text-sage font-medium" : ""}`}>{d.getDate()}</div>
              <div className="space-y-0.5 mt-1">
                {list.slice(0, 2).map((s) => (
                  <div key={s.id} onClick={(e) => { e.stopPropagation(); onSession(s); }} className="text-[10px] truncate rounded px-1.5 py-0.5"
                    style={{ backgroundColor: s.payment_status === "paid" ? "var(--sage-light)" : "#F5E5DC", color: s.payment_status === "paid" ? "var(--sage)" : "#8B4D3D" }}>
                    {new Date(s.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} {s.client_name}
                  </div>
                ))}
                {list.length > 2 && <div className="text-[10px] text-muted-soft">+{list.length - 2} autre(s)</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SessionPill({ s, onClick }) {
  const time = new Date(s.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const paid = s.payment_status === "paid";
  const cancelled = s.status === "cancelled";
  return (
    <button onClick={onClick} className="w-full text-left rounded-lg px-2.5 py-2 text-xs hover:bg-white transition-all border"
      style={{ borderColor: cancelled ? "var(--border)" : (paid ? "var(--sage)" : "#E8D5C9"), backgroundColor: cancelled ? "var(--surface)" : (paid ? "var(--surface)" : "#FBF1EA"), opacity: cancelled ? 0.6 : 1 }}
      data-testid={`session-pill-${s.id}`}>
      <div className="font-medium truncate flex items-center gap-1.5"><Clock className="w-3 h-3" strokeWidth={1.8}/> {time}</div>
      <div className="text-muted-soft truncate flex items-center gap-1.5"><User className="w-3 h-3" strokeWidth={1.8}/> {s.client_name}</div>
    </button>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg card-soft p-6 max-h-[90vh] overflow-y-auto scroll-soft">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sage-light" data-testid="modal-close"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
